// ========================================
// pages/TermsOfService.js - Terms of Service
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Legal document outlining the terms and conditions for using Agyapadie Vault
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// 
// SECTIONS:
// - Hero with title and effective date
// - Acceptance of Terms
// - Eligibility
// - Account Registration & Security
// - User Obligations
// - Asset Creation & Management
// - Beneficiaries & Trustees
// - Death Verification Process
// - Inheritance & Claims
// - Unclaimed Assets
// - Payments & Upgrades
// - Intellectual Property
// - Prohibited Conduct
// - Termination
// - Disclaimer of Warranties
// - Limitation of Liability
// - Indemnification
// - Governing Law
// - Dispute Resolution
// - Changes to Terms
// - Contact Information
// 
// RESPONSIVE BREAKPOINTS:
// - Desktop: > 1024px
// - Tablet: 768px - 1024px
// - Mobile: < 768px
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const effectiveDate = 'April 11, 2026';

  return (
    <div className="terms-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="terms-hero">
        <div className="terms-hero-overlay"></div>
        <div className="terms-container">
          <div className="terms-hero-content">
            <div className="terms-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="terms-badge-icon" />
              <span>Legal Document</span>
            </div>
            <h1 className="terms-hero-title">
              Terms of <span className="gold">Service</span>
            </h1>
            <p className="terms-hero-subtitle">
              Effective Date: <strong>{effectiveDate}</strong>
            </p>
            <p className="terms-hero-description">
              By accessing or using Agyapadie Vault, you agree to be bound by these Terms of Service. 
              Please read them carefully.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: TERMS CONTENT */}
      {/* ======================================== */}
      <section className="terms-content">
        <div className="terms-container">
          
          <div className="terms-card">
            
            {/* 1. Acceptance of Terms */}
            <div className="terms-section">
              <h2 className="terms-section-title">1. Acceptance of Terms</h2>
              <p>
                By creating an account, accessing, or using Agyapadie Vault ("the Platform"), you acknowledge 
                that you have read, understood, and agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, you may not use the Platform.
              </p>
            </div>

            {/* 2. Eligibility */}
            <div className="terms-section">
              <h2 className="terms-section-title">2. Eligibility</h2>
              <p>
                To use Agyapadie Vault, you must:
              </p>
              <ul>
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Provide accurate and complete registration information</li>
                <li>Not be prohibited from using the Platform under applicable laws</li>
              </ul>
              <div className="terms-note">
                <strong>Note:</strong> By registering, you confirm that you meet all eligibility requirements.
              </div>
            </div>

            {/* 3. Account Registration & Security */}
            <div className="terms-section">
              <h2 className="terms-section-title">3. Account Registration & Security</h2>
              <p>
                When you create an account, you agree to:
              </p>
              <ul>
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the security of your password and account credentials</li>
                <li>Notify us immediately of any unauthorized account access</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Not share your account credentials with any third party</li>
              </ul>
              <p>
                Agyapadie Vault is not liable for any loss or damage arising from your failure to 
                protect your account credentials.
              </p>
            </div>

            {/* 4. User Obligations */}
            <div className="terms-section">
              <h2 className="terms-section-title">4. User Obligations</h2>
              <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree not to:</p>
              <ul>
                <li>Use the Platform in any way that violates applicable laws or regulations</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Attempt to gain unauthorized access to any part of the Platform</li>
                <li>Interfere with or disrupt the Platform's operation</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Platform</li>
                <li>Use automated systems (bots, scrapers) to access the Platform</li>
              </ul>
            </div>

            {/* 5. Asset Creation & Management */}
            <div className="terms-section">
              <h2 className="terms-section-title">5. Asset Creation & Management</h2>
              <p>
                When creating assets on Agyapadie Vault, you acknowledge and agree that:
              </p>
              <ul>
                <li>You are solely responsible for the accuracy of asset information</li>
                <li>Assets cannot be edited after creation (immutability for certificate integrity)</li>
                <li>You may delete assets, but this action is permanent and cannot be undone</li>
                <li>Each asset generates a unique cryptographic hash and certificate number</li>
                <li>Asset certificates serve as proof of registration but are not legally binding documents</li>
                <li>You must have at least one Global Next of Kin before creating assets</li>
                <li>Each asset must have at least one beneficiary (Global NOK or External)</li>
              </ul>
              <div className="terms-note">
                <strong>Important:</strong> Agyapadie Vault is a digital vault for storing vital information. 
                It is not a substitute for legal wills or estate planning documents.
              </div>
            </div>

            {/* 6. Beneficiaries & Trustees */}
            <div className="terms-section">
              <h2 className="terms-section-title">6. Beneficiaries & Trustees</h2>
              <p>
                You are responsible for:
              </p>
              <ul>
                <li>Providing accurate contact information for beneficiaries and trustees</li>
                <li>Notifying your trustees and beneficiaries about their roles</li>
                <li>Ensuring your trustees understand their verification responsibilities</li>
                <li>Keeping beneficiary and trustee information up to date</li>
              </ul>
              <p>
                Agyapadie Vault is not responsible for:
              </p>
              <ul>
                <li>Trustees who fail to respond to verification requests</li>
                <li>Beneficiaries who fail to claim their inheritance</li>
                <li>Outdated contact information provided by you</li>
              </ul>
            </div>

            {/* 7. Death Verification Process */}
            <div className="terms-section">
              <h2 className="terms-section-title">7. Death Verification Process</h2>
              <p>
                You understand and agree that:
              </p>
              <ul>
                <li>After 90 days without clicking "I'm Alive," you become dormant</li>
                <li>Trustees will receive emails asking to verify your status</li>
                <li>Trustees who do not respond for 365 days will be removed</li>
                <li>Death verification is based on trustee responses, not official documentation</li>
                <li>Agyapadie Vault is not responsible for false verifications</li>
                <li>You may adjust your verification threshold based on your plan</li>
              </ul>
              <div className="terms-note">
                <strong>Warning:</strong> Death verification triggers asset release to beneficiaries. 
                Ensure you trust your trustees completely.
              </div>
            </div>

            {/* 8. Inheritance & Claims */}
            <div className="terms-section">
              <h2 className="terms-section-title">8. Inheritance & Claims</h2>
              <p>
                Beneficiaries acknowledge that:
              </p>
              <ul>
                <li>They must create an account using the email address notified</li>
                <li>Inherited assets are read-only and cannot be modified</li>
                <li>There is no time limit to claim assets</li>
                <li>Rejecting inheritance moves assets to the Unclaimed Assets Registry</li>
                <li>Agyapadie Vault does not guarantee the value or existence of claimed assets</li>
              </ul>
            </div>

            {/* 9. Unclaimed Assets */}
            <div className="terms-section">
              <h2 className="terms-section-title">9. Unclaimed Assets</h2>
              <p>
                You agree that:
              </p>
              <ul>
                <li>Unclaimed assets are publicly displayed as a number count only (no details)</li>
                <li>Agyapadie Vault may attempt to locate family members for unclaimed assets</li>
                <li>Assets may remain unclaimed indefinitely</li>
                <li>Agyapadie Vault is not liable for unclaimed assets</li>
              </ul>
            </div>

            {/* 10. Payments & Upgrades */}
            <div className="terms-section">
              <h2 className="terms-section-title">10. Payments & Upgrades</h2>
              <p>
                Regarding plan upgrades and PIN vouchers:
              </p>
              <ul>
                <li>All plans are ONE-TIME PAYMENT (lifetime access)</li>
                <li>No recurring subscriptions or renewal fees</li>
                <li>Downgrades are not available</li>
                <li>PIN purchases are final and non-refundable</li>
                <li>PINs are valid for one year from purchase</li>
                <li>Agyapadie Vault reserves the right to change prices with notice</li>
              </ul>
            </div>

            {/* 11. Intellectual Property */}
            <div className="terms-section">
              <h2 className="terms-section-title">11. Intellectual Property</h2>
              <p>
                All content, features, and functionality of Agyapadie Vault, including but not limited to:
              </p>
              <ul>
                <li>Software, code, algorithms, and databases</li>
                <li>Design, layout, graphics, and logos</li>
                <li>Trademarks "Agyapadie Vault" and the Golden Stool symbol</li>
                <li>Documentation and user guides</li>
              </ul>
              <p>
                are owned by Agyapadie Vault and protected by copyright, trademark, and other intellectual 
                property laws. You may not copy, modify, or distribute any part of the Platform without 
                our prior written consent.
              </p>
            </div>

            {/* 12. Prohibited Conduct */}
            <div className="terms-section">
              <h2 className="terms-section-title">12. Prohibited Conduct</h2>
              <p>You are expressly prohibited from:</p>
              <ul>
                <li>Using the Platform for any illegal purpose</li>
                <li>Uploading false or misleading asset information</li>
                <li>Attempting to manipulate the death verification process</li>
                <li>Creating fake beneficiary or trustee accounts</li>
                <li>Interfering with other users' accounts or assets</li>
                <li>Violating any applicable laws or regulations</li>
              </ul>
            </div>

            {/* 13. Termination */}
            <div className="terms-section">
              <h2 className="terms-section-title">13. Termination</h2>
              <p>
                Agyapadie Vault reserves the right to suspend or terminate your account at our sole 
                discretion, without notice, for conduct that violates these Terms or is harmful to 
                other users, us, or third parties.
              </p>
              <p>
                Upon termination:
              </p>
              <ul>
                <li>Your access to the Platform will be revoked</li>
                <li>Your data will be deleted within 30 days (except inheritance records)</li>
                <li>Inheritance records remain permanent for legal purposes</li>
                <li>No refunds will be provided for terminated accounts</li>
              </ul>
            </div>

            {/* 14. Disclaimer of Warranties */}
            <div className="terms-section">
              <h2 className="terms-section-title">14. Disclaimer of Warranties</h2>
              <p>
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES, EXPRESS OR IMPLIED. 
                AGYAPADIE VAULT DOES NOT WARRANT THAT:
              </p>
              <ul>
                <li>The Platform will be uninterrupted, secure, or error-free</li>
                <li>Any errors or defects will be corrected</li>
                <li>Asset information will be accurate or complete</li>
                <li>Beneficiaries will successfully claim assets</li>
                <li>Trustees will respond to verification requests</li>
              </ul>
              <div className="terms-note">
                <strong>Disclaimer:</strong> Agyapadie Vault is not a financial institution, legal service, 
                or certified estate planner. You are solely responsible for your estate planning decisions.
              </div>
            </div>

            {/* 15. Limitation of Liability */}
            <div className="terms-section">
              <h2 className="terms-section-title">15. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AGYAPADIE VAULT AND ITS OFFICERS, DIRECTORS, 
                EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION:
              </p>
              <ul>
                <li>Loss of assets, profits, or data</li>
                <li>Unauthorized access to your account</li>
                <li>Trustee or beneficiary non-response</li>
                <li>Failure of death verification process</li>
                <li>Any claims arising from your use of the Platform</li>
              </ul>
              <p>
                OUR TOTAL LIABILITY TO YOU SHALL NOT EXCEED THE AMOUNT YOU PAID TO US (IF ANY) FOR THE 
                SIX MONTHS PRECEDING THE CLAIM.
              </p>
            </div>

            {/* 16. Indemnification */}
            <div className="terms-section">
              <h2 className="terms-section-title">16. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Agyapadie Vault from any claims, 
                damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) 
                arising from:
              </p>
              <ul>
                <li>Your use of the Platform</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Any content you submit to the Platform</li>
              </ul>
            </div>

            {/* 17. Governing Law */}
            <div className="terms-section">
              <h2 className="terms-section-title">17. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the 
                Republic of Ghana, without regard to its conflict of law provisions. The courts of 
                Ghana shall have exclusive jurisdiction over any disputes arising from these Terms.
              </p>
            </div>

            {/* 18. Dispute Resolution */}
            <div className="terms-section">
              <h2 className="terms-section-title">18. Dispute Resolution</h2>
              <p>
                Before filing a claim against Agyapadie Vault, you agree to:
              </p>
              <ul>
                <li>Contact us at <a href="mailto:legal@agyapadie.com">legal@agyapadie.com</a> to describe the dispute</li>
                <li>Allow 30 days for informal resolution</li>
                <li>Attempt good-faith negotiations before legal action</li>
              </ul>
              <p>
                If the dispute cannot be resolved informally, either party may submit the dispute to 
                binding arbitration in Accra, Ghana, in accordance with the Alternative Dispute Resolution 
                Act, 2010 (Act 798).
              </p>
            </div>

            {/* 19. Changes to Terms */}
            <div className="terms-section">
              <h2 className="terms-section-title">19. Changes to Terms</h2>
              <p>
                Agyapadie Vault reserves the right to modify these Terms at any time. Material changes 
                will be notified via:
              </p>
              <ul>
                <li>Email to registered account holders</li>
                <li>A notice on the Platform dashboard</li>
                <li>Updated "Effective Date" at the top of this page</li>
              </ul>
              <p>
                Your continued use of the Platform after changes constitutes acceptance of the modified Terms.
              </p>
            </div>

            {/* 20. Contact Information */}
            <div className="terms-section">
              <h2 className="terms-section-title">20. Contact Information</h2>
              <p>For questions about these Terms, please contact us at:</p>
              <div className="terms-contact">
                <p><strong>Legal Department</strong></p>
                <p><strong>Email:</strong> <a href="mailto:legal@agyapadie.com">legal@agyapadie.com</a></p>
                <p><strong>Address:</strong> 123 Independence Avenue, Ridge, Accra, Ghana</p>
                <p><strong>Phone:</strong> +233 (0) 30 212 3456</p>
              </div>
            </div>

          </div>
          
          {/* Back to Home Link */}
          <div className="terms-back">
            <Link to="/" className="terms-back-link">← Back to Home</Link>
          </div>
          
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES - Fully Responsive */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .terms-page {
          font-family: 'Montserrat', sans-serif;
          background: #F5F0E6;
          min-height: 100vh;
        }
        
        .terms-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        /* Hero Section */
        .terms-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .terms-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .terms-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .terms-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .terms-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .terms-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .terms-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 15px;
        }
        
        .terms-hero-subtitle {
          font-size: 16px;
          color: #FFD700;
          margin-bottom: 20px;
        }
        
        .terms-hero-description {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Content Section */
        .terms-content {
          padding: 60px 0;
        }
        
        .terms-card {
          background: white;
          border-radius: 15px;
          padding: 50px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }
        
        .terms-section {
          margin-bottom: 40px;
          padding-bottom: 30px;
          border-bottom: 1px solid #eee;
        }
        
        .terms-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        
        .terms-section-title {
          font-size: 24px;
          color: #0A0A0A;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #FFD700;
          display: inline-block;
        }
        
        .terms-section p {
          color: #444;
          line-height: 1.7;
          margin-bottom: 15px;
        }
        
        .terms-section ul {
          margin: 15px 0 15px 30px;
        }
        
        .terms-section li {
          color: #555;
          line-height: 1.7;
          margin-bottom: 8px;
        }
        
        .terms-note {
          background: #F5F0E6;
          padding: 15px 20px;
          border-radius: 10px;
          margin: 20px 0;
          border-left: 4px solid #FFD700;
          font-size: 14px;
        }
        
        .terms-contact {
          background: #F5F0E6;
          padding: 20px;
          border-radius: 10px;
          margin-top: 15px;
        }
        
        .terms-contact p {
          margin-bottom: 8px;
        }
        
        .terms-contact a {
          color: #FFD700;
          text-decoration: none;
        }
        
        .terms-contact a:hover {
          text-decoration: underline;
        }
        
        .terms-section a {
          color: #FFD700;
          text-decoration: none;
        }
        
        .terms-section a:hover {
          text-decoration: underline;
        }
        
        .terms-back {
          text-align: center;
          margin-top: 40px;
        }
        
        .terms-back-link {
          display: inline-block;
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
          transition: color 0.3s;
        }
        
        .terms-back-link:hover {
          color: #FFD700;
        }
        
        /* ======================================== */
        /* RESPONSIVE BREAKPOINTS */
        /* ======================================== */
        
        /* Tablet: 1024px and below */
        @media (max-width: 1024px) {
          .terms-hero-title {
            font-size: 40px;
          }
          
          .terms-card {
            padding: 40px;
          }
        }
        
        /* Mobile: 768px and below */
        @media (max-width: 768px) {
          .terms-hero-title {
            font-size: 32px;
          }
          
          .terms-hero-description {
            font-size: 16px;
          }
          
          .terms-card {
            padding: 25px;
          }
          
          .terms-section-title {
            font-size: 20px;
          }
          
          .terms-section p,
          .terms-section li {
            font-size: 14px;
          }
          
          .terms-section ul {
            margin-left: 20px;
          }
        }
        
        /* Small Mobile: 480px and below */
        @media (max-width: 480px) {
          .terms-hero-title {
            font-size: 28px;
          }
          
          .terms-hero-description {
            font-size: 14px;
          }
          
          .terms-card {
            padding: 20px;
          }
          
          .terms-section-title {
            font-size: 18px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default TermsOfService;